import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { getAIAnalysis, getPredictiveAnalysis, getOptimizedPrices } from "../lib/api";
import { AIModelSelector } from "../components/AIModelSelector";
import { toast } from "sonner";
import { 
  Sparkles, 
  TrendingUp, 
  TrendingDown,
  BarChart3, 
  Swords, 
  Loader2, 
  Calendar,
  Target,
  AlertTriangle,
  CheckCircle,
  ArrowUpRight,
  ArrowDownRight,
  Minus
} from "lucide-react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  ReferenceLine
} from "recharts";

const analysisTypes = [
  { value: "general", label: "Análisis General", icon: BarChart3, description: "Visión general del rendimiento" },
  { value: "pricing", label: "Estrategia de Precios", icon: TrendingUp, description: "Optimización de tarifas" },
  { value: "demand", label: "Predicción de Demanda", icon: Sparkles, description: "Pronóstico de ocupación" },
  { value: "competitor", label: "Análisis Competitivo", icon: Swords, description: "Posición en el mercado" },
];

const AIAnalysis = () => {
  const [activeTab, setActiveTab] = useState("analysis");
  const [selectedType, setSelectedType] = useState("general");
  const [analysis, setAnalysis] = useState(null);
  const [predictive, setPredictive] = useState(null);
  const [optimized, setOptimized] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingPredictive, setLoadingPredictive] = useState(false);
  const [loadingOptimized, setLoadingOptimized] = useState(false);
  const [aiModel, setAiModel] = useState(() => localStorage.getItem("ai_model") || "claude");
  const changeAiModel = (m) => { setAiModel(m); localStorage.setItem("ai_model", m); };

  const handleAnalyze = async () => {
    setLoading(true);
    try {
      const response = await getAIAnalysis(selectedType, aiModel);
      setAnalysis(response.data);
    } catch (error) {
      console.error("Error getting AI analysis:", error);
      toast.error("Error al obtener análisis");
    } finally {
      setLoading(false);
    }
  };

  const handlePredictive = async () => {
    setLoadingPredictive(true);
    try {
      const response = await getPredictiveAnalysis(14);
      setPredictive(response.data);
      toast.success("Análisis predictivo generado");
    } catch (error) {
      console.error("Error getting predictive analysis:", error);
      toast.error("Error al obtener predicciones");
    } finally {
      setLoadingPredictive(false);
    }
  };

  const handleOptimize = async () => {
    setLoadingOptimized(true);
    try {
      const response = await getOptimizedPrices();
      setOptimized(response.data);
      toast.success("Precios optimizados calculados");
    } catch (error) {
      console.error("Error optimizing prices:", error);
      toast.error("Error al optimizar precios");
    } finally {
      setLoadingOptimized(false);
    }
  };

  const formatAnalysis = (text) => {
    if (!text) return null;
    const lines = text.split("\n");
    return lines.map((line, index) => {
      const lineKey = `line-${index}-${line.slice(0, 20).replace(/\s/g, '')}`;
      if (line.startsWith("##") || line.startsWith("**")) {
        const cleanLine = line.replace(/^#+\s*/, "").replace(/\*\*/g, "");
        return <h3 key={lineKey} className="font-semibold text-lg mt-4 mb-2">{cleanLine}</h3>;
      }
      if (line.trim().startsWith("-") || line.trim().startsWith("•") || line.trim().startsWith("*")) {
        const indent = line.match(/^\s*/)[0].length;
        const cleanLine = line.trim().replace(/^[-•*]\s*/, "").replace(/\*\*/g, "");
        return <li key={lineKey} className={`${indent >= 2 ? "ml-8" : "ml-4"} mb-1 list-disc`}>{cleanLine}</li>;
      }
      if (line.trim()) {
        return <p key={lineKey} className="mb-2">{line.replace(/\*\*/g, "")}</p>;
      }
      return null;
    });
  };

  const selectedTypeInfo = analysisTypes.find(t => t.value === selectedType);

  const getUrgencyColor = (urgency) => {
    switch(urgency) {
      case "alta": return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400";
      case "media": return "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400";
      default: return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400";
    }
  };

  const getActionIcon = (action) => {
    if (action.includes("SUBIR")) return <ArrowUpRight className="w-4 h-4 text-green-500" />;
    if (action.includes("BAJAR")) return <ArrowDownRight className="w-4 h-4 text-red-500" />;
    return <Minus className="w-4 h-4 text-gray-500" />;
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="ai-analysis-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Análisis IA</h1>
        <p className="text-muted-foreground">Inteligencia artificial para optimizar tus ingresos</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="analysis" className="flex items-center gap-2">
            <Sparkles className="w-4 h-4" />
            Análisis General
          </TabsTrigger>
          <TabsTrigger value="predictive" className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Predicción Demanda
          </TabsTrigger>
          <TabsTrigger value="optimize" className="flex items-center gap-2">
            <Target className="w-4 h-4" />
            Optimizar Precios
          </TabsTrigger>
        </TabsList>

        {/* Tab 1: Análisis General */}
        <TabsContent value="analysis" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {analysisTypes.map((type) => (
              <Card
                key={type.value}
                className={`cursor-pointer transition-all ${
                  selectedType === type.value 
                    ? "border-primary bg-primary/5 ring-2 ring-primary" 
                    : "hover:border-primary/50"
                }`}
                onClick={() => setSelectedType(type.value)}
                data-testid={`analysis-type-${type.value}`}
              >
                <CardContent className="pt-6">
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      selectedType === type.value ? "bg-primary text-primary-foreground" : "bg-muted"
                    }`}>
                      <type.icon className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="font-semibold">{type.label}</h3>
                      <p className="text-sm text-muted-foreground">{type.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <div className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground">Modelo:</span>
              <AIModelSelector value={aiModel} onChange={changeAiModel} testIdPrefix="analysis-model" />
            </div>
            <Button size="lg" onClick={handleAnalyze} disabled={loading} className="px-8" data-testid="generate-analysis-btn">
              {loading ? (
                <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Analizando...</>
              ) : (
                <><Sparkles className="w-5 h-5 mr-2" />Generar Análisis de {selectedTypeInfo?.label}</>
              )}
            </Button>
          </div>

          {analysis && (
            <Card className="ai-card animate-fade-in" data-testid="analysis-result">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div>
                    <CardTitle>Análisis de {selectedTypeInfo?.label}</CardTitle>
                    <p className="text-sm text-muted-foreground" data-testid="analysis-generated-by">Generado por {analysis.model?.label || "Claude"} {analysis.model?.model ? `(${analysis.model.model})` : "AI"}</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="prose prose-sm dark:prose-invert max-w-none">
                  {formatAnalysis(analysis.analysis)}
                </div>
              </CardContent>
            </Card>
          )}

          {!analysis && !loading && (
            <Card className="bg-muted/50">
              <CardContent className="py-12 text-center">
                <Sparkles className="w-12 h-12 mx-auto text-amber-500 mb-4" />
                <h3 className="text-lg font-semibold mb-2">Análisis Potenciado por IA</h3>
                <p className="text-muted-foreground max-w-md mx-auto">
                  Selecciona un tipo de análisis y haz clic en "Generar" para obtener 
                  recomendaciones personalizadas basadas en los datos de tu hotel.
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Tab 2: Predicción de Demanda */}
        <TabsContent value="predictive" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-blue-500" />
                Predicción de Demanda (14 días)
              </CardTitle>
              <CardDescription>
                Analiza patrones históricos para predecir la ocupación futura y recomendar acciones
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button onClick={handlePredictive} disabled={loadingPredictive} data-testid="generate-predictive-btn">
                {loadingPredictive ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Analizando patrones...</>
                ) : (
                  <><TrendingUp className="w-4 h-4 mr-2" />Generar Predicciones</>
                )}
              </Button>
            </CardContent>
          </Card>

          {predictive?.success && (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-3xl font-bold text-blue-600">{predictive.summary.avg_predicted_occupancy}%</p>
                      <p className="text-sm text-muted-foreground">Ocupación Media Prevista</p>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-3xl font-bold text-green-600">{predictive.summary.high_demand_days}</p>
                      <p className="text-sm text-muted-foreground">Días Alta Demanda</p>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-3xl font-bold text-amber-600">{predictive.summary.low_demand_days}</p>
                      <p className="text-sm text-muted-foreground">Días Baja Demanda</p>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-3xl font-bold">{predictive.summary.days_analyzed}</p>
                      <p className="text-sm text-muted-foreground">Días Analizados</p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Predictions Chart */}
              <Card>
                <CardHeader>
                  <CardTitle>Predicción de Ocupación</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={predictive.predictions}>
                        <defs>
                          <linearGradient id="predGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <XAxis 
                          dataKey="date" 
                          tickFormatter={(v) => v.split("-").slice(1).join("/")}
                          tick={{ fontSize: 11 }}
                        />
                        <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 11 }} />
                        <Tooltip 
                          formatter={(value, name) => [`${value}%`, name === "predicted_occupancy" ? "Predicción" : "Actual"]}
                          labelFormatter={(label, payload) => payload?.[0]?.payload?.day_name + " " + label}
                        />
                        <ReferenceLine y={70} stroke="#22c55e" strokeDasharray="3 3" label="Alta" />
                        <ReferenceLine y={40} stroke="#f59e0b" strokeDasharray="3 3" label="Baja" />
                        <Area 
                          type="monotone" 
                          dataKey="predicted_occupancy" 
                          stroke="#3b82f6" 
                          fill="url(#predGradient)"
                          strokeWidth={2}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Predictions Table */}
              <Card>
                <CardHeader>
                  <CardTitle>Acciones Recomendadas por Día</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 px-2">Fecha</th>
                          <th className="text-left py-2 px-2">Día</th>
                          <th className="text-right py-2 px-2">Ocupación Actual</th>
                          <th className="text-right py-2 px-2">Predicción</th>
                          <th className="text-right py-2 px-2">Disponibles</th>
                          <th className="text-center py-2 px-2">Acción</th>
                          <th className="text-center py-2 px-2">Urgencia</th>
                        </tr>
                      </thead>
                      <tbody>
                        {predictive.predictions.map((pred) => (
                          <tr key={`pred-${pred.date}`} className={`border-b ${pred.is_weekend ? "bg-blue-50/50 dark:bg-blue-900/10" : ""}`}>
                            <td className="py-2 px-2">{pred.date.split("-").slice(1).join("/")}</td>
                            <td className="py-2 px-2">{pred.day_name}</td>
                            <td className="text-right py-2 px-2">{pred.current_occupancy}%</td>
                            <td className="text-right py-2 px-2 font-medium">{pred.predicted_occupancy}%</td>
                            <td className="text-right py-2 px-2">{pred.available_rooms}</td>
                            <td className="text-center py-2 px-2">
                              <Badge variant="outline" className="flex items-center gap-1 justify-center">
                                {getActionIcon(pred.price_recommendation)}
                                {pred.price_recommendation}
                              </Badge>
                            </td>
                            <td className="text-center py-2 px-2">
                              <Badge className={getUrgencyColor(pred.urgency)}>{pred.urgency}</Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>

              {/* AI Insights */}
              {predictive.ai_insights && (
                <Card className="border-amber-200 dark:border-amber-800">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-amber-500" />
                      Análisis IA
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="prose prose-sm dark:prose-invert max-w-none">
                      {formatAnalysis(predictive.ai_insights)}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>

        {/* Tab 3: Optimizar Precios */}
        <TabsContent value="optimize" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="w-5 h-5 text-green-500" />
                Optimización de Precios
              </CardTitle>
              <CardDescription>
                Calcula el precio óptimo para cada tipo de habitación basado en ocupación, históricos y competencia
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button onClick={handleOptimize} disabled={loadingOptimized} data-testid="optimize-prices-btn">
                {loadingOptimized ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Calculando precios óptimos...</>
                ) : (
                  <><Target className="w-4 h-4 mr-2" />Calcular Precios Óptimos</>
                )}
              </Button>
            </CardContent>
          </Card>

          {optimized?.success && (
            <>
              {/* Summary */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="border-green-200 dark:border-green-800">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <ArrowUpRight className="w-8 h-8 text-green-500" />
                      <div>
                        <p className="text-2xl font-bold">{optimized.summary.rooms_to_increase}</p>
                        <p className="text-sm text-muted-foreground">Subir precio</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-red-200 dark:border-red-800">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <ArrowDownRight className="w-8 h-8 text-red-500" />
                      <div>
                        <p className="text-2xl font-bold">{optimized.summary.rooms_to_decrease}</p>
                        <p className="text-sm text-muted-foreground">Bajar precio</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-8 h-8 text-blue-500" />
                      <div>
                        <p className="text-2xl font-bold">{optimized.summary.rooms_optimal}</p>
                        <p className="text-sm text-muted-foreground">Precio óptimo</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-amber-200 dark:border-amber-800">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <TrendingUp className="w-8 h-8 text-amber-500" />
                      <div>
                        <p className="text-2xl font-bold">+€{optimized.summary.potential_daily_increase}</p>
                        <p className="text-sm text-muted-foreground">Potencial/día</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Recommendations */}
              <Card>
                <CardHeader>
                  <CardTitle>Recomendaciones por Habitación</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {optimized.recommendations.map((rec) => (
                      <div 
                        key={`rec-${rec.room_type_id || rec.room_name}`} 
                        className={`p-4 rounded-lg border ${
                          rec.action === "SUBIR" ? "border-green-200 bg-green-50/50 dark:border-green-800 dark:bg-green-900/10" :
                          rec.action === "BAJAR" ? "border-red-200 bg-red-50/50 dark:border-red-800 dark:bg-red-900/10" :
                          "border-gray-200 dark:border-gray-800"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-semibold">{rec.room_name}</h4>
                            <p className="text-sm text-muted-foreground">
                              Ocupación: {rec.occupancy}%
                            </p>
                          </div>
                          <div className="text-right">
                            <div className="flex items-center gap-2">
                              <span className="text-muted-foreground">€{rec.current_price}</span>
                              <span>→</span>
                              <span className="font-bold text-lg">€{rec.optimal_price}</span>
                              {rec.difference !== 0 && (
                                <Badge className={rec.difference > 0 ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}>
                                  {rec.difference > 0 ? "+" : ""}{rec.difference_pct}%
                                </Badge>
                              )}
                            </div>
                            <div className="text-xs text-muted-foreground mt-1">
                              {rec.reasons.join(" • ")}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AIAnalysis;
